import React from "react";
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { MaintenanceState } from "api/automationTypes";
import { BlockStandard } from "components/admin/BlockStandard";
import { PhotoUploadGrid } from "./PhotoUploadGrid";
import { DeleteCTA } from "components/MUIExtensions/DeleteCTA";

export type SystemType =
  | "Refrigeradores"
  | "Freezers"
  | "Câmaras frigoríficas"
  | "Resfriadores de Líquidos/Chillers";

export const SYSTEM_TYPES: SystemType[] = [
  "Refrigeradores",
  "Freezers",
  "Câmaras frigoríficas",
  "Resfriadores de Líquidos/Chillers",
];

const CONSERVATION_STATES = Object.values(MaintenanceState);

export type RefrigerationDetailProps = {
  refrigerationData: RefrigerationDetailItem;
  refrigerationPos: number;
  onChange: (refrigeration: RefrigerationDetailItem) => void;
  onDelete?: () => void;
};

export type RefrigerationDetailItem = {
  systemType: SystemType | "";
  equipmentType: string; // Tipo de equipamento / tecnologia
  nominalPower: string; // Potência nominal (kW)
  quantity: string; // Quantidade
  capacity: string; // Capacidade (L)
  conservationStatus: MaintenanceState | "";
  obs: string; // Observações
  pictures: File[];
};

export const emptyRefrigeration: RefrigerationDetailItem = {
  systemType: "",
  equipmentType: "",
  nominalPower: "",
  quantity: "",
  capacity: "",
  conservationStatus: "",
  obs: "",
  pictures: [],
};

export const RefrigerationDetail = ({
  refrigerationData,
  refrigerationPos,
  onChange,
  onDelete,
}: RefrigerationDetailProps) => {
  const handleSystemTypeChange = ({ target }) => {
    onChange({
      ...refrigerationData,
      systemType: target.value,
    });
  };

  const handleEquipmentTypeChange = ({ target }) => {
    onChange({
      ...refrigerationData,
      equipmentType: target.value,
    });
  };

  const handleNominalPowerChange = ({ target }) => {
    onChange({
      ...refrigerationData,
      nominalPower: target.value,
    });
  };

  const handleQuantityChange = ({ target }) => {
    onChange({
      ...refrigerationData,
      quantity: target.value,
    });
  };

  const handleCapacityChange = ({ target }) => {
    onChange({
      ...refrigerationData,
      capacity: target.value,
    });
  };

  const handleConservationStatusChange = ({ target }) => {
    onChange({
      ...refrigerationData,
      conservationStatus: target.value,
    });
  };

  const handleObsChange = ({ target }) => {
    onChange({
      ...refrigerationData,
      obs: target.value,
    });
  };

  const handlePhotosChange = (photos: File[]) => {
    onChange({
      ...refrigerationData,
      pictures: photos,
    });
  };

  return (
    <BlockStandard>
      <FormControl fullWidth>
        <InputLabel id={`select-system-type-${refrigerationPos}`}>
          Tipo de sistema
        </InputLabel>
        <Select
          labelId={`select-system-type-${refrigerationPos}`}
          label="Tipo de sistema"
          value={refrigerationData.systemType || ""}
          onChange={handleSystemTypeChange}
        >
          {SYSTEM_TYPES.map((option) => (
            <MenuItem value={option} key={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        fullWidth
        label="Tipo de equipamento / tecnologia"
        value={refrigerationData.equipmentType || ""}
        onChange={handleEquipmentTypeChange}
      />

      <Grid
        item
        container
        direction="row"
        justifyContent="space-between"
        gap={2}
      >
        <TextField
          label="Potência nominal (kW)"
          placeholder="Ex.: 9.000"
          value={refrigerationData.nominalPower || ""}
          onChange={handleNominalPowerChange}
          sx={{ flex: 1 }}
        />

        <TextField
          label="Quantidade"
          placeholder="Ex.: 1"
          value={refrigerationData.quantity || ""}
          onChange={handleQuantityChange}
          sx={{ flex: 1 }}
        />
      </Grid>

      <Grid
        item
        container
        direction="row"
        justifyContent="space-between"
        gap={2}
      >
        <TextField
          label="Capacidade (L)"
          placeholder="Ex.: 450"
          value={refrigerationData.capacity || ""}
          onChange={handleCapacityChange}
          sx={{ flex: 1 }}
        />

        <FormControl sx={{ flex: 1 }}>
          <InputLabel id={`select-conservation-${refrigerationPos}`}>
            Estado de conservação
          </InputLabel>
          <Select
            labelId={`select-conservation-${refrigerationPos}`}
            label="Estado de conservação"
            value={refrigerationData.conservationStatus || ""}
            onChange={handleConservationStatusChange}
          >
            {CONSERVATION_STATES.map((option) => (
              <MenuItem value={option} key={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <TextField
        fullWidth
        multiline
        minRows={3}
        label="Observações"
        placeholder="Descreva sua observação"
        value={refrigerationData.obs || ""}
        onChange={handleObsChange}
      />

      <PhotoUploadGrid
        photos={refrigerationData.pictures}
        onPhotosChange={handlePhotosChange}
        buttonText="Enviar foto"
      />

      {onDelete && <DeleteCTA onDeleteConfirmed={onDelete} />}
    </BlockStandard>
  );
};
